/**
 * @fileoverview Hook Data Layer para carga y gestión de recordatorios de cliente en Inbox.
 * @module useCustomerRemindersData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import remindersModelMC from '../../../models/RemindersModelMC';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { sharingInformationService } from '../../../../services/sharingInformationService';

/**
 * @typedef {Object} UseCustomerRemindersDataResult
 * @property {boolean} loading - Estado de carga
 * @property {Array} customerReminders - Lista de recordatorios del cliente
 * @property {function} reloadReminders - Función para recargar los recordatorios
 */

/**
 * Hook Data Layer para carga y gestión de recordatorios de cliente en Inbox.
 *
 * - Solo gestiona carga de datos y estados de loading/error.
 * - Optimizado con useCallback y separación estricta de responsabilidades.
 *
 * @param {Object} customer - Objeto customer con id
 * @returns {UseCustomerRemindersDataResult}
 * @see {@link useCustomerRemindersUI} Para gestión de UI
 */
export default function useCustomerRemindersData(customer) {
  const dispatch = useDispatch();
  const { t } = useTranslation('Inbox');
  const [loading, setLoading] = useState(false);
  const [customerReminders, setCustomerReminders] = useState([]);

  const reloadReminders = useCallback(async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const reminders = await remindersModelMC.getReminders(customer?.id);
      setCustomerReminders(reminders?.reminders || []);
    } catch (error) {
      const errorMessage = error?.message || error || t('reminders.error_loading');
      dispatch({ type: LOAD_DATA_FAILURE, error: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [customer?.id, dispatch, t]);

  useEffect(() => {
    if (!customer || !customer.id) return;
    reloadReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();
    const myObservable = subscription$.subscribe((data) => {
      if (data.refreshListDataReminder) {
        reloadReminders();
      }
    });
    return () => {
      myObservable.unsubscribe();
    };
  }, []);

  return { loading, customerReminders, reloadReminders, setCustomerReminders };
}
